import React from 'react';
import SectionHeader from './SectionHeader';
import { formatAddress } from './AddressFields';
import {
  COMMUNICATION_FREQUENCIES,
  EMPLOYMENT_STATUSES,
  OCCUPATION_OTHER,
} from './options';
import { filledRows, formatCents, sumCents } from './percent';
import { hasSpouse, validateStep } from './validation';

const DASH = '—';

const text = (v) => {
  const t = String(v ?? '').trim();
  return t || DASH;
};

const yesNo = (v) => {
  if (v === 'yes') return 'Yes';
  if (v === 'no') return 'No';
  return DASH;
};

const date = (v) => {
  const raw = String(v ?? '').trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  return match ? `${match[2]}/${match[3]}/${match[1]}` : (raw || DASH);
};

const country = (value, other) => (
  value === 'other' ? text(other) : (value === 'usa' ? 'USA' : DASH)
);

const employmentLabel = (status) => (
  EMPLOYMENT_STATUSES.find((s) => s.value === status)?.label || DASH
);

const frequencyLabel = (value) => (
  COMMUNICATION_FREQUENCIES.find((o) => o.value === value)?.label || DASH
);

/** Employed and self-employed both name a business; the wording differs. */
const HAS_EMPLOYER = ['employed', 'self_employed'];

function Row({ label, value }) {
  return (
    <div className="d-flex justify-content-between gap-3 py-1 border-bottom">
      <span className="text-muted small">{label}</span>
      <span className="text-end small fw-semibold">{value}</span>
    </div>
  );
}

function Card({ title, onEdit, children }) {
  return (
    <div className="cashflow-section-card mb-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="cashflow-subsection-title mb-0">{title}</div>
        {onEdit && (
          <button type="button" className="btn btn-link btn-sm p-0" onClick={onEdit}>
            <i className="bi bi-pencil me-1" />
            Edit
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

const tcpHasData = (t) => ['full_name', 'relationship', 'phone', 'email']
  .some((k) => String(t?.[k] ?? '').trim())
  || ['street', 'street2', 'city', 'state', 'zip']
    .some((k) => String(t?.address?.[k] ?? '').trim());

function BeneficiaryGroup({ label, rows }) {
  const filled = filledRows(rows);
  if (filled.length === 0) {
    return <Row label={`${label} beneficiaries`} value="None listed" />;
  }
  const total = sumCents(filled);
  return (
    <div className="mt-2">
      <div className="d-flex justify-content-between align-items-center">
        <span className="text-muted small fw-semibold">{label}</span>
        <span className={`badge ${total === 10000 ? 'bg-success' : 'bg-danger'}`}>
          {formatCents(total)}%
        </span>
      </div>
      {filled.map((r, i) => (
        <Row
          key={i}
          label={`${text(r.full_name)} · ${text(r.relationship)}`}
          value={`${text(r.pct)}% · DOB ${date(r.dob)}`}
        />
      ))}
    </div>
  );
}

function PersonSummary({ heading, prefix, formData, spousePresent, onJump }) {
  const key = (base) => (prefix ? `${prefix}_${base}` : base);
  const contact = formData[key('contact')] || {};
  const employment = formData[key('employment')] || {};
  const compliance = formData[key('compliance')] || {};
  const tcp = formData[key('trusted_contact')] || {};
  const bene = formData[key('beneficiaries')] || {};
  const skipPrimary = spousePresent && bene.spouse_is_sole_primary === 'yes';
  // The secondary TCP is optional, so only show its rows once it is filled.
  const secondaryTcp = tcpHasData(tcp.secondary) ? tcp.secondary : null;

  return (
    <div className="mb-4">
      <h3 className="h6 fw-bold text-uppercase text-muted mb-3">{heading}</h3>

      <Card title="Contact Information" onEdit={() => onJump(key('contact'))}>
        <Row label="Full legal name" value={text(contact.full_name)} />
        <Row label="Preferred name" value={text(contact.preferred_name)} />
        {contact.known_by_other_name === 'yes' && (
          <Row label="Also known as" value={text(contact.other_name)} />
        )}
        <Row label="Date of birth" value={date(contact.date_of_birth)} />
        <Row label="Phone" value={text(contact.phone)} />
        <Row label="Email" value={text(contact.email)} />
        <Row label="Legal / home address" value={text(formatAddress(contact.address))} />
        <Row label="Mother's maiden name" value={text(contact.mothers_maiden_name)} />
        <Row label="Security question" value={text(contact.security_question)} />
        <Row label="Citizenship" value={country(contact.citizenship, contact.citizenship_other)} />
        <Row label="Legal residence" value={country(contact.residence, contact.residence_other)} />
        <Row label="Country of birth" value={country(contact.birth_country, contact.birth_country_other)} />
      </Card>

      <Card title="Employment" onEdit={() => onJump(key('employment'))}>
        <Row label="Status" value={employmentLabel(employment.status)} />
        {HAS_EMPLOYER.includes(employment.status) && (
          <>
            <Row
              label={employment.status === 'self_employed' ? 'Business' : 'Employer'}
              value={text(employment.employer_name)}
            />
            <Row
              label={employment.status === 'self_employed' ? 'Business address' : 'Employer address'}
              value={text(formatAddress(employment.employer_address))}
            />
          </>
        )}
        {employment.occupation && (
          <Row
            label="Occupation"
            value={
              employment.occupation === OCCUPATION_OTHER
                ? text(employment.occupation_other)
                : text(employment.occupation)
            }
          />
        )}
      </Card>

      <Card title="Compliance & Reporting" onEdit={() => onJump(key('compliance'))}>
        <Row label="Exchange / FINRA affiliated" value={yesNo(compliance.exchange_affiliated)} />
        {compliance.exchange_affiliated === 'yes' && (
          <Row label="Firm & relationship" value={text(compliance.exchange_affiliated_details)} />
        )}
        <Row label="Director / 10% shareholder" value={yesNo(compliance.public_company_insider)} />
        {compliance.public_company_insider === 'yes' && (
          <Row label="Company & ticker" value={text(compliance.public_company_insider_details)} />
        )}
        <Row
          label="Paperless delivery"
          value={compliance.paperless_delivery === 'yes' ? 'Yes — via client portal' : 'No — postal mail'}
        />
        <Row
          label="Quarterly reports"
          value={compliance.quarterly_reports_portal === 'yes' ? 'Yes — via client portal' : 'No — postal mail'}
        />
        <Row
          label="How often you hear from us"
          value={frequencyLabel(compliance.communication_frequency)}
        />
      </Card>

      <Card title="Trusted Contact Person" onEdit={() => onJump(key('trusted_contact'))}>
        <Row label="Name" value={text(tcp.full_name)} />
        <Row label="Relationship" value={text(tcp.relationship)} />
        <Row label="Phone" value={text(tcp.phone)} />
        <Row label="Email" value={text(tcp.email)} />
        <Row label="Address" value={text(formatAddress(tcp.address))} />
        {secondaryTcp && (
          <>
            <Row label="Secondary name" value={text(secondaryTcp.full_name)} />
            <Row label="Secondary relationship" value={text(secondaryTcp.relationship)} />
            <Row label="Secondary phone" value={text(secondaryTcp.phone)} />
            <Row label="Secondary email" value={text(secondaryTcp.email)} />
            <Row label="Secondary address" value={text(formatAddress(secondaryTcp.address))} />
          </>
        )}
      </Card>

      <Card title="Beneficiaries" onEdit={() => onJump(key('beneficiaries'))}>
        {bene.same_as_primary === true && (
          <Row label="Beneficiaries" value="Same as the first person listed" />
        )}
        {spousePresent && (
          <Row
            label="Spouse/partner is 100% primary"
            value={yesNo(bene.spouse_is_sole_primary)}
          />
        )}
        {!skipPrimary && <BeneficiaryGroup label="Primary" rows={bene.primary} />}
        <BeneficiaryGroup label="Contingent" rows={bene.contingent} />
      </Card>
    </div>
  );
}

/**
 * Final review. Lists everything back to the client with per-section Edit
 * links, and surfaces any remaining validation problems before they submit.
 */
export default function StepReview({ number, formData, steps, onJump }) {
  const spousePresent = hasSpouse(formData);

  const outstanding = steps
    .filter((s) => (s.person !== 'spouse' || spousePresent))
    .map((s) => ({ step: s, message: validateStep(s.key, formData) }))
    .filter((x) => x.message);

  return (
    <div>
      <SectionHeader
        number={number}
        title="Review & Submit"
        tip="Please check everything below. You can come back and change any of it before your Getting Started meeting."
      />

      {outstanding.length > 0 && (
        <div className="alert alert-warning">
          <p className="fw-semibold mb-2">
            <i className="bi bi-exclamation-triangle me-2" />
            A few things still need your attention:
          </p>
          <ul className="mb-0 small">
            {outstanding.map(({ step, message }) => (
              <li key={step.key}>
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 align-baseline"
                  onClick={() => onJump(step.key)}
                >
                  {step.label}
                </button>
                {' — '}
                {message}
              </li>
            ))}
          </ul>
        </div>
      )}

      <PersonSummary
        heading="Your Information"
        prefix=""
        formData={formData}
        spousePresent={spousePresent}
        onJump={onJump}
      />

      {spousePresent && (
        <PersonSummary
          heading="Your Spouse / Partner"
          prefix="spouse"
          formData={formData}
          spousePresent={spousePresent}
          onJump={onJump}
        />
      )}

      <p className="text-muted small">
        When you press Submit we will let your Client Services team know your
        Personal Data Sheet is ready.
      </p>
    </div>
  );
}
